import { arrayMove } from "@dnd-kit/sortable";
import type { Todo, TodoListDayData } from "./schema";

/**
 * Every transition over one day's stored todo array, every view derived from
 * it, and — since docs/adr/0024 — the day's «Ferdig for i dag» marker, whose
 * one invariant is stated in terms of that array.
 *
 * A huskeliste keeps one stored order that only dragging changes
 * (docs/adr/0015). Two things render over that array without rewriting it: the
 * done/not-done split, and — since docs/adr/0020 — deletion, which leaves the
 * item in place as a tombstone so restoring returns it to the exact slot it
 * held. Both are views, which is why the array is the only thing ever written.
 *
 * The derivations return `readonly Todo[]` deliberately. TypeScript refuses to
 * pass one to a writer here, and that refusal is the whole defence: a write
 * built from a filtered list would silently drop every tombstone, emptying the
 * trash with nothing failing loudly.
 */

/** A todo removed from the list but kept in the array (docs/adr/0020). */
export function isDeleted(todo: Todo): boolean {
  return todo.deletionOrder !== undefined;
}

/** Everything the card may render: the array minus its tombstones. */
export function visibleTodos(todos: readonly Todo[]): readonly Todo[] {
  return todos.filter((t) => !isDeleted(t));
}

/** Open items, in stored order — the only group a drag can reach. */
export function openTodos(todos: readonly Todo[]): readonly Todo[] {
  return visibleTodos(todos).filter((t) => !t.checked);
}

/**
 * Done items in Fullføringsrekkefølge: newest tick first.
 *
 * Todos ticked before completion order existed carry no stamp and sort last,
 * among themselves in stored order — which is exactly how they render today,
 * so no historical day changes appearance until something new is ticked
 * (docs/adr/0019).
 */
export function doneTodos(todos: readonly Todo[]): readonly Todo[] {
  const done = visibleTodos(todos).filter((t) => t.checked);
  const stamped = done.filter((t) => t.completionOrder !== undefined);
  const unstamped = done.filter((t) => t.completionOrder === undefined);
  stamped.sort((a, b) => (b.completionOrder ?? 0) - (a.completionOrder ?? 0));
  return [...stamped, ...unstamped];
}

/** The trash, newest deletion first — the mis-tap you want back is row one. */
export function deletedTodos(todos: readonly Todo[]): readonly Todo[] {
  return todos
    .filter(isDeleted)
    .slice()
    .sort((a, b) => (b.deletionOrder ?? 0) - (a.deletionOrder ?? 0));
}

/** Visible items ticked off, for the collapsed card's summary line. */
export function checkedCount(todos: readonly Todo[]): number {
  return visibleTodos(todos).filter((t) => t.checked).length;
}

/** «Ferdig for i dag» (CONTEXT.md): the day has been closed. */
export function isFinished(day: TodoListDayData): boolean {
  return day.finished === true;
}

/**
 * Ferdiglogget (CONTEXT.md): nothing unchecked, and either something visible
 * on the list or the day closed by «Ferdig for i dag».
 *
 * Reading the raw array here would let a day emptied by deleting claim the
 * finish line, since tombstones keep its length above zero while nothing
 * remains unchecked. Deleting is not doing — closing is, which is why the
 * marker counts and a bare empty list does not (docs/adr/0024).
 *
 * The marker is honoured only while nothing is open. Every transition below
 * keeps that true, but two devices merging their writes need not, and a green
 * card over an unchecked row is worse than a marker quietly ignored.
 */
export function loggingCompleted(day: TodoListDayData): boolean {
  const visible = visibleTodos(day.todos);
  if (visible.some((t) => !t.checked)) return false;
  return visible.length > 0 || isFinished(day);
}

/**
 * Put a new stored array on the day.
 *
 * An open item on a closed day is a contradiction (docs/adr/0024), so the
 * marker survives a write only while nothing is open afterwards. One rule
 * here rather than one per transition: adding, unticking, restoring and
 * receiving a move all reopen the day for the same reason, and renaming,
 * dragging, ticking and deleting all leave it closed for the same reason.
 */
export function withTodos(
  day: TodoListDayData,
  todos: Todo[],
): TodoListDayData {
  const { finished, ...rest } = day;
  const keep = finished === true && openTodos(todos).length === 0;
  return keep ? { ...rest, todos, finished: true } : { ...rest, todos };
}

/**
 * «Ferdig for i dag» by hand. Refused while anything is open: the card only
 * offers the button on an empty list, and this guard keeps the rule true for
 * any caller.
 */
export function closeDay(day: TodoListDayData): TodoListDayData {
  if (openTodos(day.todos).length > 0) return day;
  return { ...day, finished: true };
}

/** «Åpne dagen igjen»: drops the marker and brings nothing back. */
export function reopenDay(day: TodoListDayData): TodoListDayData {
  const { finished: _dropped, ...rest } = day;
  return rest;
}

/**
 * Take moved items off the day they are leaving.
 *
 * The one write that closes a day rather than reopening it: when nothing open
 * is left behind, carrying the last open item away is the act of closing
 * (docs/adr/0024). Deleting the last item goes through `remove` and claims
 * nothing. A move is a genuine splice, not a tombstone — the item lives on
 * another day now, so there is nothing here to restore.
 */
export function moveOut(
  day: TodoListDayData,
  ids: ReadonlySet<string>,
): TodoListDayData {
  const remaining = day.todos.filter((t) => !ids.has(t.id));
  const next = withTodos(day, remaining);
  const carriedSomething = remaining.length < day.todos.length;
  return carriedSomething ? closeDay(next) : next;
}

/**
 * Land moved items at the bottom of the day they are arriving on. They are
 * open items, so a closed target day reopens — `withTodos` sees to it.
 */
export function moveIn(
  day: TodoListDayData,
  arriving: readonly Todo[],
): TodoListDayData {
  return withTodos(day, [...day.todos, ...arriving]);
}

function nextOrdinal(values: readonly (number | undefined)[]): number {
  const used = values.filter((v): v is number => v !== undefined);
  return used.length === 0 ? 1 : Math.max(...used) + 1;
}

/** Tick an item, stamping it after every item ticked before it. */
export function tick(todos: readonly Todo[], id: string): Todo[] {
  const next = nextOrdinal(todos.map((t) => t.completionOrder));
  return todos.map((t) =>
    t.id === id ? { ...t, checked: true, completionOrder: next } : t,
  );
}

/**
 * Untick an item, discarding its stamp.
 *
 * Re-ticking therefore mints a fresh one and the item returns to the top of
 * the done group — correct, because re-ticking is a second completion. A
 * restore is not a completion at all and never touches this field.
 */
export function untick(todos: readonly Todo[], id: string): Todo[] {
  return todos.map((t) => {
    if (t.id !== id) return t;
    const { completionOrder: _dropped, ...rest } = t;
    return { ...rest, checked: false };
  });
}

/** Tick or untick, whichever the item is not. */
export function toggleChecked(todos: readonly Todo[], id: string): Todo[] {
  const target = todos.find((t) => t.id === id);
  if (!target) return todos.slice();
  return target.checked ? untick(todos, id) : tick(todos, id);
}

/**
 * Delete an item, leaving it in place as a tombstone.
 *
 * Only an unticked item can be deleted (docs/adr/0020) — the card gives done
 * rows no delete control, and this guard keeps the rule true for any caller.
 */
export function remove(todos: readonly Todo[], id: string): Todo[] {
  const target = todos.find((t) => t.id === id);
  if (!target || target.checked || isDeleted(target)) return todos.slice();
  const next = nextOrdinal(todos.map((t) => t.deletionOrder));
  return todos.map((t) => (t.id === id ? { ...t, deletionOrder: next } : t));
}

/** Restore a tombstone to the exact slot it never left. */
export function restore(todos: readonly Todo[], id: string): Todo[] {
  return todos.map((t) => {
    if (t.id !== id || !isDeleted(t)) return t;
    const { deletionOrder: _dropped, ...rest } = t;
    return rest;
  });
}

/** Append a new open item at the end of the stored order. */
export function append(todos: readonly Todo[], label: string): Todo[] {
  return [...todos, { id: crypto.randomUUID(), label, checked: false }];
}

/** Rename an item, leaving every ordinal alone. */
export function rename(
  todos: readonly Todo[],
  id: string,
  label: string,
): Todo[] {
  return todos.map((t) => (t.id === id ? { ...t, label } : t));
}

/**
 * Move one item to where another sits in the stored array.
 *
 * Both ids come from the open group, but the indices are resolved against the
 * whole array so that done items and tombstones sitting invisibly between them
 * keep their relative order (docs/adr/0015).
 *
 * Delegates to dnd-kit's own `arrayMove` rather than reimplementing it, so the
 * committed order is by construction the one the drag preview animated.
 */
export function reorder(
  todos: readonly Todo[],
  activeId: string,
  overId: string,
): Todo[] {
  const from = todos.findIndex((t) => t.id === activeId);
  const to = todos.findIndex((t) => t.id === overId);
  if (from < 0 || to < 0 || from === to) return todos.slice();
  return arrayMove(todos.slice(), from, to);
}
